import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import {
  vehicleFleetApproveReservation,
  vehicleFleetCancelReservation,
  vehicleFleetLoadReservations,
  vehicleFleetLoadVehicles,
  vehicleFleetRejectReservation,
  type VehicleFleetReservation,
  type VehicleFleetVehicle,
} from '../../lib/apiPhpAdapter'

type Props = {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  timeFrom?: string
  timeTo?: string
  /** Správce IT / administrátor autoparku — může zrušit jakoukoli rezervaci. */
  canManage?: boolean
  /** Ředitel / IT — schvaluje žádosti (admin autoparku jen vidí). */
  canApprove?: boolean
}

const WEEKDAYS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne']
const DAY_START_H = 6
const DAY_END_H = 22

function ymd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function formatCzechDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  return dt.toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

function monthMatrix(year: number, month: number): (Date | null)[][] {
  const first = new Date(year, month, 1)
  const startDow = (first.getDay() + 6) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: (Date | null)[] = []
  for (let i = 0; i < startDow; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d))
  while (cells.length % 7 !== 0) cells.push(null)
  const rows: (Date | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7))
  return rows
}

function toMinutes(dt: string): number {
  const m = dt.replace('T', ' ').match(/(\d{2}):(\d{2})/)
  if (!m) return 0
  return Number(m[1]) * 60 + Number(m[2])
}

function formatHm(min: number): string {
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function occupiesSlot(r: VehicleFleetReservation): boolean {
  return r.status === 'active' || r.status === 'pending'
}

/** Rezervace / žádost zasahuje do kalendářního dne (celý den). */
function touchesDay(r: VehicleFleetReservation, day: string): boolean {
  if (!occupiesSlot(r)) return false
  const start = r.starts_at.replace('T', ' ').slice(0, 10)
  const end = r.ends_at.replace('T', ' ').slice(0, 10)
  return start <= day && end >= day
}

function dayStyle(busy: number, total: number, selected: boolean): CSSProperties {
  let bg = '#f8fafc'
  let border = '#e2e8f0'
  if (total > 0) {
    if (busy <= 0) {
      bg = '#dcfce7'
      border = '#22c55e'
    } else if (busy >= total) {
      bg = '#fecaca'
      border = '#ef4444'
    } else {
      bg = '#fde68a'
      border = '#f59e0b'
    }
  }
  return {
    border: selected ? '2px solid #0f766e' : `2px solid ${border}`,
    borderRadius: 10,
    padding: '0.45rem 0.25rem',
    background: bg,
    cursor: 'pointer',
    minHeight: 56,
    boxShadow: selected ? '0 0 0 3px rgba(15, 118, 110, 0.2)' : undefined,
  }
}

export function VehicleFleetCalendarPanel({
  showAlert,
  canManage = false,
  canApprove = false,
}: Props) {
  const now = new Date()
  const [cursor, setCursor] = useState(() => new Date(now.getFullYear(), now.getMonth(), 1))
  const [selectedDay, setSelectedDay] = useState(() => ymd(now))
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null)
  const [reservations, setReservations] = useState<VehicleFleetReservation[]>([])
  const [vehicles, setVehicles] = useState<VehicleFleetVehicle[]>([])
  const [loading, setLoading] = useState(true)
  const [busyCancel, setBusyCancel] = useState(false)
  const [busyReview, setBusyReview] = useState(false)

  const reload = async () => {
    const [resList, vehList] = await Promise.all([
      vehicleFleetLoadReservations({ fleet: true }),
      vehicleFleetLoadVehicles(true),
    ])
    setReservations(Array.isArray(resList) ? resList : [])
    setVehicles(Array.isArray(vehList) ? vehList.filter((v) => !!v.active) : [])
  }

  const cancelReservation = async (id: number, label: string) => {
    if (!window.confirm(`Zrušit rezervaci${label ? ` (${label})` : ''}?`)) return
    setBusyCancel(true)
    try {
      await vehicleFleetCancelReservation(id)
      await reload()
      showAlert('Rezervace zrušena', 'success')
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Zrušení selhalo', 'error')
    } finally {
      setBusyCancel(false)
    }
  }

  const reviewReservation = async (id: number, action: 'approve' | 'reject') => {
    const ok = window.confirm(
      action === 'approve' ? 'Schválit tuto žádost o rezervaci?' : 'Zamítnout tuto žádost?',
    )
    if (!ok) return
    setBusyReview(true)
    try {
      if (action === 'approve') await vehicleFleetApproveReservation(id)
      else await vehicleFleetRejectReservation(id)
      await reload()
      showAlert(action === 'approve' ? 'Žádost schválena' : 'Žádost zamítnuta', 'success')
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Akce selhala', 'error')
    } finally {
      setBusyReview(false)
    }
  }

  const year = cursor.getFullYear()
  const month = cursor.getMonth()
  const rows = useMemo(() => monthMatrix(year, month), [year, month])
  const activeVehicles = useMemo(() => vehicles.filter((v) => !!v.active), [vehicles])

  const statsByDay = useMemo(() => {
    const map = new Map<string, { busy: number; free: number; total: number }>()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    for (let d = 1; d <= daysInMonth; d++) {
      const key = ymd(new Date(year, month, d))
      let busy = 0
      for (const v of activeVehicles) {
        if (reservations.some((r) => Number(r.vehicle_id) === v.id && touchesDay(r, key))) {
          busy++
        }
      }
      const total = activeVehicles.length
      map.set(key, { busy, free: Math.max(0, total - busy), total })
    }
    return map
  }, [reservations, activeVehicles, year, month])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        await reload()
      } catch (e: unknown) {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Kalendář se nepodařilo načíst', 'error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [showAlert])

  const allActive = useMemo(
    () => reservations.filter((r) => r.status === 'active'),
    [reservations],
  )

  const pendingRequests = useMemo(
    () => reservations.filter((r) => r.status === 'pending'),
    [reservations],
  )

  const vehicleBusy = useMemo(() => {
    const map = new Map<number, boolean>()
    for (const v of activeVehicles) {
      map.set(
        v.id,
        reservations.some((r) => Number(r.vehicle_id) === v.id && touchesDay(r, selectedDay)),
      )
    }
    return map
  }, [activeVehicles, reservations, selectedDay])

  const selectedVehicle = activeVehicles.find((v) => v.id === selectedVehicleId) || null

  const vehicleSchedule = useMemo(() => {
    if (!selectedVehicleId) return []
    return reservations
      .filter(
        (r) =>
          occupiesSlot(r) && Number(r.vehicle_id) === selectedVehicleId && touchesDay(r, selectedDay),
      )
      .map((r) => {
        let rs = toMinutes(r.starts_at)
        let re = toMinutes(r.ends_at)
        const rStartDay = r.starts_at.replace('T', ' ').slice(0, 10)
        const rEndDay = r.ends_at.replace('T', ' ').slice(0, 10)
        if (rStartDay < selectedDay) rs = 0
        if (rEndDay > selectedDay) re = 24 * 60
        return { ...r, rs, re, multiDay: rStartDay !== rEndDay }
      })
      .sort((a, b) => a.rs - b.rs)
  }, [reservations, selectedVehicleId, selectedDay])

  const dayList = useMemo(
    () => reservations.filter((r) => touchesDay(r, selectedDay)),
    [reservations, selectedDay],
  )

  const title = cursor.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })
  const spanMin = (DAY_END_H - DAY_START_H) * 60
  const selectedStats = statsByDay.get(selectedDay) || { busy: 0, free: 0, total: 0 }

  return (
    <section
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: '1.25rem 1.4rem',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      }}
    >
      <h2 style={{ margin: '0 0 4px' }}>Přehled rezervací</h2>
      <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 12px', lineHeight: 1.45 }}>
        1) Klikněte na den → 2) vyberte vozidlo → 3) uvidíte, kdy je obsazené.
        {canManage ? ' Jako správce můžete zrušit jakoukoli rezervaci.' : ''}
        {canApprove
          ? ' Žádosti o rezervaci můžete schválit nebo zamítnout.'
          : canManage
            ? ' Žádosti o rezervaci schvaluje ředitel — zde je pouze vidíte.'
            : ''}
      </p>

      {canManage && pendingRequests.length > 0 ? (
        <div
          style={{
            marginBottom: 16,
            padding: '0.85rem 1rem',
            borderRadius: 10,
            border: '1px solid #fcd34d',
            background: '#fffbeb',
          }}
        >
          <h3 style={{ margin: '0 0 8px', fontSize: '1rem' }}>
            {canApprove
              ? `Žádosti ke schválení (${pendingRequests.length})`
              : `Žádosti čekající na ředitele (${pendingRequests.length})`}
          </h3>
          <p style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#92400e', lineHeight: 1.4 }}>
            Řidič není stálým řidičem vozidla — rezervace platí až po schválení ředitelem.
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {pendingRequests.map((r) => (
              <li
                key={r.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: '0.55rem 0',
                  borderBottom: '1px solid #fde68a',
                  fontSize: '0.9rem',
                }}
              >
                <div>
                  <strong>
                    {r.vehicle_name} · {r.plate_number}
                  </strong>
                  <div style={{ color: '#475569' }}>
                    {r.starts_at.replace('T', ' ').slice(0, 16)} →{' '}
                    {r.ends_at.replace('T', ' ').slice(0, 16)}
                  </div>
                  {r.driver_name ? (
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>{r.driver_name}</div>
                  ) : null}
                  {r.note ? (
                    <div style={{ color: '#78716c', fontSize: '0.85rem' }}>{r.note}</div>
                  ) : null}
                </div>
                {canApprove ? (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="system-button"
                      disabled={busyReview}
                      onClick={() => void reviewReservation(r.id, 'approve')}
                    >
                      Schválit
                    </button>
                    <button
                      type="button"
                      disabled={busyReview}
                      style={{ color: '#b91c1c' }}
                      onClick={() => void reviewReservation(r.id, 'reject')}
                    >
                      Zamítnout
                    </button>
                  </div>
                ) : (
                  <span style={{ fontSize: '0.8rem', color: '#92400e', flexShrink: 0 }}>Ke schválení</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {canManage ? (
        <div
          style={{
            marginBottom: 16,
            padding: '0.85rem 1rem',
            borderRadius: 10,
            border: '1px solid #fecaca',
            background: '#fff7ed',
          }}
        >
          <h3 style={{ margin: '0 0 8px', fontSize: '1rem' }}>
            Všechny aktivní rezervace ({allActive.length})
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 280, overflow: 'auto' }}>
            {allActive.map((r) => (
              <li
                key={r.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: '0.5rem 0',
                  borderBottom: '1px solid #fed7aa',
                  fontSize: '0.9rem',
                }}
              >
                <div>
                  <strong>
                    {r.vehicle_name} · {r.plate_number}
                  </strong>
                  <div style={{ color: '#475569' }}>
                    {r.starts_at.replace('T', ' ').slice(0, 16)} →{' '}
                    {r.ends_at.replace('T', ' ').slice(0, 16)}
                  </div>
                  {r.driver_name ? (
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                      {r.driver_name}
                      {r.is_assigned_driver_reservation ? ' · rezervace stálého řidiče' : ''}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  disabled={busyCancel}
                  style={{ color: '#b91c1c', flexShrink: 0 }}
                  onClick={() =>
                    void cancelReservation(
                      r.id,
                      `${r.vehicle_name || ''} ${r.starts_at.replace('T', ' ').slice(0, 16)}`,
                    )
                  }
                >
                  Zrušit
                </button>
              </li>
            ))}
            {allActive.length === 0 ? (
              <li style={{ color: '#64748b', padding: '0.35rem 0' }}>Žádné aktivní rezervace.</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 12,
          fontSize: '0.85rem',
        }}
      >
        <Legend color="#dcfce7" border="#22c55e" text="Vše volné" />
        <Legend color="#fde68a" border="#f59e0b" text="Něco rezervováno" />
        <Legend color="#fecaca" border="#ef4444" text="Vše obsazeno" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <button type="button" onClick={() => setCursor(new Date(year, month - 1, 1))}>
          ← Předchozí
        </button>
        <strong style={{ textTransform: 'capitalize', flex: 1, textAlign: 'center', fontSize: '1.05rem' }}>
          {title}
        </strong>
        <button type="button" onClick={() => setCursor(new Date(year, month + 1, 1))}>
          Další →
        </button>
      </div>

      {loading ? (
        <p>Načítání…</p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: 6,
            marginBottom: 18,
          }}
        >
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              style={{ textAlign: 'center', fontSize: '0.8rem', color: '#64748b', fontWeight: 700, paddingBottom: 4 }}
            >
              {w}
            </div>
          ))}
          {rows.flatMap((week, wi) =>
            week.map((day, di) => {
              if (!day) return <div key={`e-${wi}-${di}`} />
              const key = ymd(day)
              const st = statsByDay.get(key) || { busy: 0, free: 0, total: 0 }
              const selected = key === selectedDay
              const isToday = key === ymd(now)
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setSelectedDay(key)
                    setSelectedVehicleId(null)
                  }}
                  style={dayStyle(st.busy, st.total, selected)}
                >
                  <div style={{ fontWeight: isToday ? 800 : 700, fontSize: '1rem' }}>{day.getDate()}</div>
                  {st.total > 0 ? (
                    <div style={{ fontSize: '0.7rem', color: '#334155', marginTop: 2 }}>
                      {st.free === st.total
                        ? 'volno'
                        : st.busy === st.total
                          ? 'plno'
                          : `${st.free} volné`}
                    </div>
                  ) : null}
                </button>
              )
            }),
          )}
        </div>
      )}

      <div
        style={{
          padding: '0.85rem 1rem',
          borderRadius: 10,
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          marginBottom: 14,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4, textTransform: 'capitalize' }}>
          {formatCzechDay(selectedDay)}
        </div>
        <div style={{ fontSize: '0.9rem', color: '#475569' }}>
          Volných vozidel: <strong style={{ color: '#15803d' }}>{selectedStats.free}</strong>
          {' · '}
          Obsazených: <strong style={{ color: '#b91c1c' }}>{selectedStats.busy}</strong>
          {' · '}
          Celkem: {selectedStats.total}
        </div>
        {dayList.length > 0 ? (
          <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#64748b' }}>
            V tento den běží {dayList.length} rezervací (včetně vícedenních).
          </div>
        ) : (
          <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#15803d' }}>
            Žádné rezervace — všechna vozidla jsou tento den volná.
          </div>
        )}
      </div>

      <h3 style={{ fontSize: '1.05rem', margin: '0 0 10px' }}>Všechna vozidla</h3>
      <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
        {activeVehicles.map((v) => {
          const busy = vehicleBusy.get(v.id)
          const selected = selectedVehicleId === v.id
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => setSelectedVehicleId(v.id)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                padding: '0.75rem 0.9rem',
                borderRadius: 10,
                border: selected ? '2px solid #0d9488' : '1px solid #e2e8f0',
                background: busy ? '#fff1f2' : '#f0fdf4',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span>
                <strong style={{ fontSize: '1rem' }}>{v.name}</strong>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 2 }}>
                  {v.model} · {v.plate_number}
                  {v.assigned_driver_name ? ` · stálý: ${v.assigned_driver_name}` : ''}
                </div>
              </span>
              <span
                style={{
                  flexShrink: 0,
                  padding: '0.25rem 0.55rem',
                  borderRadius: 999,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  background: busy ? '#fecaca' : '#bbf7d0',
                  color: busy ? '#991b1b' : '#166534',
                }}
              >
                {busy ? 'Obsazeno' : 'Volné'}
              </span>
            </button>
          )
        })}
        {activeVehicles.length === 0 ? (
          <p style={{ color: '#64748b', margin: 0 }}>Zatím nejsou žádná aktivní vozidla.</p>
        ) : null}
      </div>

      {selectedVehicle ? (
        <div
          style={{
            border: '1px solid #99f6e4',
            borderRadius: 12,
            padding: '1rem 1.1rem',
            background: '#f0fdfa',
          }}
        >
          <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>
            Graf: {selectedVehicle.name}
          </h3>
          <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#0f766e' }}>
            {selectedVehicle.plate_number} · {formatCzechDay(selectedDay)}
            {selectedVehicle.assigned_driver_name
              ? ` · stálý řidič: ${selectedVehicle.assigned_driver_name}`
              : ''}
          </p>

          <div
            style={{
              position: 'relative',
              height: 64,
              borderRadius: 10,
              background: '#bbf7d0',
              border: '1px solid #4ade80',
              overflow: 'hidden',
              marginBottom: 6,
            }}
          >
            {Array.from({ length: DAY_END_H - DAY_START_H + 1 }, (_, i) => DAY_START_H + i).map((h) => {
              const left = ((h * 60 - DAY_START_H * 60) / spanMin) * 100
              return (
                <div
                  key={h}
                  style={{
                    position: 'absolute',
                    left: `${left}%`,
                    top: 0,
                    bottom: 0,
                    width: 1,
                    background: 'rgba(15, 23, 42, 0.1)',
                  }}
                />
              )
            })}
            {vehicleSchedule.map((r) => {
              const clipStart = Math.max(r.rs, DAY_START_H * 60)
              const clipEnd = Math.min(r.re, DAY_END_H * 60)
              if (clipEnd <= clipStart) return null
              const left = ((clipStart - DAY_START_H * 60) / spanMin) * 100
              const width = ((clipEnd - clipStart) / spanMin) * 100
              const pending = r.status === 'pending'
              return (
                <div
                  key={r.id}
                  title={`${formatHm(r.rs)}–${formatHm(r.re)}${pending ? ' (čeká na schválení)' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${Math.max(width, 1.5)}%`,
                    top: 10,
                    bottom: 10,
                    borderRadius: 6,
                    background: pending ? '#f59e0b' : '#ef4444',
                    border: pending ? '1px solid #d97706' : '1px solid #b91c1c',
                  }}
                />
              )
            })}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '0.75rem',
              color: '#64748b',
              marginBottom: 12,
            }}
          >
            <span>6:00</span>
            <span>12:00</span>
            <span>22:00</span>
          </div>

          {vehicleSchedule.length === 0 ? (
            <p style={{ margin: 0, color: '#166534', fontWeight: 600 }}>Tento den je vozidlo volné.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {vehicleSchedule.map((r) => (
                <li
                  key={r.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    alignItems: 'flex-start',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid #ccfbf1',
                    fontSize: '0.9rem',
                  }}
                >
                  <div>
                    <strong style={{ color: r.status === 'pending' ? '#b45309' : '#b91c1c' }}>
                      {r.multiDay
                        ? `${r.starts_at.replace('T', ' ').slice(0, 16)} → ${r.ends_at.replace('T', ' ').slice(0, 16)}`
                        : `${formatHm(r.rs)}–${formatHm(r.re)}`}
                    </strong>
                    {r.status === 'pending' ? (
                      <span style={{ marginLeft: 6, fontSize: '0.75rem', color: '#b45309' }}>
                        (čeká na schválení)
                      </span>
                    ) : null}
                    {r.multiDay ? (
                      <span style={{ marginLeft: 6, fontSize: '0.75rem', color: '#0f766e' }}>
                        (vícedenní)
                      </span>
                    ) : null}
                    {r.driver_name ? (
                      <div style={{ color: '#475569' }}>
                        {r.driver_name}
                        {r.is_assigned_driver_reservation ? (
                          <span style={{ marginLeft: 6, fontSize: '0.75rem', color: '#9a3412' }}>
                            (stálý řidič)
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    {r.note ? <div style={{ color: '#64748b' }}>{r.note}</div> : null}
                  </div>
                  {canManage ? (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                      {r.status === 'pending' ? (
                        canApprove ? (
                          <>
                            <button
                              type="button"
                              className="system-button"
                              disabled={busyReview}
                              onClick={() => void reviewReservation(r.id, 'approve')}
                            >
                              Schválit
                            </button>
                            <button
                              type="button"
                              disabled={busyReview}
                              style={{ color: '#b91c1c' }}
                              onClick={() => void reviewReservation(r.id, 'reject')}
                            >
                              Zamítnout
                            </button>
                          </>
                        ) : (
                          <span style={{ fontSize: '0.8rem', color: '#92400e' }}>Ke schválení</span>
                        )
                      ) : (
                        <button
                          type="button"
                          disabled={busyCancel}
                          style={{ color: '#b91c1c' }}
                          onClick={() => void cancelReservation(r.id, formatHm(r.rs))}
                        >
                          Zrušit
                        </button>
                      )}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <p
          style={{
            margin: 0,
            padding: '0.75rem 0.9rem',
            borderRadius: 8,
            background: '#f1f5f9',
            color: '#64748b',
            fontSize: '0.9rem',
          }}
        >
          Klikněte na vozidlo výše — zobrazí se denní graf obsazenosti.
        </p>
      )}
    </section>
  )
}

function Legend({ color, border, text }: { color: string; border: string; text: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569' }}>
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: 4,
          background: color,
          border: `2px solid ${border}`,
        }}
      />
      {text}
    </span>
  )
}
